import React, { useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import {
  SafeAreaView,
  useSafeAreaInsets,
  type Edge,
} from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import TonyteBackground from '../components/TonyteBackground';
import { Colors, Typography, Spacing, Radius } from '../theme';
import type { RootStackParamList } from '../types';
import { useI18n } from '../i18n';
import { resolveLegalContent, type LegalSection } from '../data/legalContent/types';
import { impressumContent } from '../data/legalContent/impressum';
import { communityGuidelinesContent } from '../data/legalContent/communityGuidelines';
import { partnerTermsContent } from '../data/legalContent/partnerTerms';
import { termsOfServiceContent } from '../data/legalContent/termsOfService';
import { privacyPolicyContent } from '../data/legalContent/privacyPolicy';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

const LAST_UPDATED_DATE = '8 August 2026';
const CONTACT_EMAIL = 'hello@tonyte.app';
const LEGAL_SAFE_AREA_EDGES: Edge[] =
  Platform.OS === 'ios' ? ['left', 'right'] : [];

// ─── Shared primitives ────────────────────────────────────────────────────────

function LegalHeader({
  title,
  subtitle,
  iconName,
  onBack,
  paddingTop,
}: {
  title: string;
  subtitle: string;
  iconName: string;
  onBack: () => void;
  paddingTop: number;
}) {
  const { theme } = useTheme();
  const appearance = useMemo(() => createLegalThemeStyles(theme), [theme]);
  return (
    <View style={[headerStyles.root, { paddingTop }]}>
      <TouchableOpacity
        style={[headerStyles.backBtn, appearance.control]}
        onPress={onBack}
        activeOpacity={0.75}
      >
        <Icon name="arrow-left" size={20} color={theme.colors.icon} />
        <Text style={[headerStyles.backLabel, appearance.textSecondary]}>Back</Text>
      </TouchableOpacity>

      <View style={[headerStyles.card, appearance.card]}>
        <LinearGradient
          pointerEvents="none"
          colors={!theme.isDark
            ? ['rgba(255,255,255,0.82)', 'rgba(244,225,198,0.42)', 'rgba(255,255,255,0.12)']
            : ['rgba(38,208,206,0.14)', 'rgba(161,140,209,0.10)', 'rgba(4,8,15,0.0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        <View style={[headerStyles.cardBorder, appearance.border]} />
        <View style={headerStyles.frame}>
          <View style={headerStyles.content}>
            <View style={headerStyles.titleRow}>
              <View style={[headerStyles.iconWrap, appearance.control]}>
                <Icon name={iconName} size={22} color={theme.colors.accentSecondary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[headerStyles.appName, appearance.textMuted]}>TONYTE</Text>
                <Text style={[headerStyles.title, appearance.textPrimary]}>{title}</Text>
              </View>
            </View>
            <Text style={[headerStyles.subtitle, appearance.textSecondary]} selectable>
              {subtitle}
            </Text>
            <View style={[headerStyles.datePill, appearance.control]}>
              <Text style={[headerStyles.dateText, appearance.textMuted]}>
                Last updated: {LAST_UPDATED_DATE}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

function Section({
  title,
  iconName,
  children,
}: {
  title: string;
  iconName: string;
  children: React.ReactNode;
}) {
  const { theme } = useTheme();
  const appearance = useMemo(() => createLegalThemeStyles(theme), [theme]);
  return (
    <View style={[sectionStyles.card, appearance.card]}>
      <LinearGradient
        pointerEvents="none"
        colors={!theme.isDark
          ? ['rgba(255,255,255,0.78)', 'rgba(244,225,198,0.34)']
          : ['rgba(255,255,255,0.045)', 'rgba(4,8,15,0.72)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View style={[sectionStyles.border, appearance.border]} />
      <View style={sectionStyles.frame}>
        <View style={sectionStyles.content}>
          <View style={sectionStyles.titleRow}>
            <View style={[sectionStyles.iconWrap, appearance.control]}>
              <Icon name={iconName} size={16} color={theme.colors.icon} />
            </View>
            <Text style={[sectionStyles.title, appearance.textPrimary]}>{title}</Text>
          </View>
          <View style={sectionStyles.body}>{children}</View>
        </View>
      </View>
    </View>
  );
}

function Para({ children }: { children: string }) {
  const { theme } = useTheme();
  return (
    <Text style={[sectionStyles.para, daylightStyle(theme, { color: theme.colors.textSecondary })]} selectable>
      {children}
    </Text>
  );
}

function Bullets({ items }: { items: string[] }) {
  const { theme } = useTheme();
  return (
    <View style={sectionStyles.bullets}>
      {items.map((item, i) => (
        <View key={i} style={sectionStyles.bulletRow}>
          <View style={[sectionStyles.dot, daylightStyle(theme, { backgroundColor: theme.colors.accent })]} />
          <Text style={[sectionStyles.bulletText, daylightStyle(theme, { color: theme.colors.textSecondary })]} selectable>
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function renderLegalSections(sections: LegalSection[]) {
  return sections.map((section) => (
    <Section key={section.title} title={section.title} iconName={section.iconName}>
      {section.paragraphs.map((paragraph, index) => (
        <React.Fragment key={index}>
          <Para>{paragraph}</Para>
          {section.bulletsAfterParagraph?.[index] && (
            <Bullets items={section.bulletsAfterParagraph[index]} />
          )}
        </React.Fragment>
      ))}
    </Section>
  ));
}

function ContactFooter({ paddingBottom }: { paddingBottom: number }) {
  const { theme } = useTheme();
  const appearance = useMemo(() => createLegalThemeStyles(theme), [theme]);
  return (
    <View
      style={[footerStyles.card, appearance.card, { marginBottom: paddingBottom + Spacing.xl }]}
    >
      <LinearGradient
        pointerEvents="none"
        colors={!theme.isDark
          ? ['rgba(255,255,255,0.78)', 'rgba(244,225,198,0.34)']
          : ['rgba(255,255,255,0.045)', 'rgba(4,8,15,0.72)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View style={[footerStyles.border, appearance.border]} />
      <View style={footerStyles.frame}>
        <View style={footerStyles.content}>
          <View style={footerStyles.row}>
            <Icon name="mail" size={15} color={theme.colors.icon} />
            <Text style={[footerStyles.title, appearance.textPrimary]}>Contact</Text>
          </View>
          <Text style={[footerStyles.body, appearance.textSecondary]} selectable>
            {'For questions, contact us at '}
            <Text style={[footerStyles.email, appearance.textPrimary]}>{CONTACT_EMAIL}</Text>
            {'.'}
          </Text>
        </View>
      </View>
    </View>
  );
}

// ─── Terms of Service ─────────────────────────────────────────────────────────

type TOSProps = NativeStackScreenProps<RootStackParamList, 'TermsOfService'>;

export const TermsOfServiceScreen: React.FC<TOSProps> = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const { language } = useI18n();
  const content = resolveLegalContent(termsOfServiceContent, language);

  return (
    <TonyteBackground appearanceAware style={styles.root}>
      <SafeAreaView edges={LEGAL_SAFE_AREA_EDGES} style={styles.safeArea}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <LegalHeader
            title={content.title}
            subtitle={content.subtitle}
            iconName={content.iconName}
            onBack={() => navigation.goBack()}
            paddingTop={insets.top + Spacing.sm}
          />

          <View style={styles.sections}>{renderLegalSections(content.sections)}</View>

          <ContactFooter paddingBottom={insets.bottom} />
        </ScrollView>
      </SafeAreaView>
    </TonyteBackground>
  );
};

// ─── Privacy Policy ───────────────────────────────────────────────────────────

type PPProps = NativeStackScreenProps<RootStackParamList, 'PrivacyPolicy'>;

export const PrivacyPolicyScreen: React.FC<PPProps> = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const { language } = useI18n();
  const content = resolveLegalContent(privacyPolicyContent, language);

  return (
    <TonyteBackground appearanceAware style={styles.root}>
      <SafeAreaView edges={LEGAL_SAFE_AREA_EDGES} style={styles.safeArea}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <LegalHeader
            title={content.title}
            subtitle={content.subtitle}
            iconName={content.iconName}
            onBack={() => navigation.goBack()}
            paddingTop={insets.top + Spacing.sm}
          />

          <View style={styles.sections}>{renderLegalSections(content.sections)}</View>

          <ContactFooter paddingBottom={insets.bottom} />
        </ScrollView>
      </SafeAreaView>
    </TonyteBackground>
  );
};

// ─── Impressum ────────────────────────────────────────────────────────────────

type ImpressumProps = NativeStackScreenProps<RootStackParamList, 'Impressum'>;

export const ImpressumScreen: React.FC<ImpressumProps> = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const { language } = useI18n();
  const content = resolveLegalContent(impressumContent, language);

  return (
    <TonyteBackground appearanceAware style={styles.root}>
      <SafeAreaView edges={LEGAL_SAFE_AREA_EDGES} style={styles.safeArea}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <LegalHeader
            title={content.title}
            subtitle={content.subtitle}
            iconName={content.iconName}
            onBack={() => navigation.goBack()}
            paddingTop={insets.top + Spacing.sm}
          />

          <View style={styles.sections}>{renderLegalSections(content.sections)}</View>

          <ContactFooter paddingBottom={insets.bottom} />
        </ScrollView>
      </SafeAreaView>
    </TonyteBackground>
  );
};

// ─── Partner Terms ────────────────────────────────────────────────────────────

type PartnerTermsProps = NativeStackScreenProps<
  RootStackParamList,
  'PartnerTerms'
>;

export const PartnerTermsScreen: React.FC<PartnerTermsProps> = ({
  navigation,
}) => {
  const insets = useSafeAreaInsets();
  const { language } = useI18n();
  const content = resolveLegalContent(partnerTermsContent, language);

  return (
    <TonyteBackground appearanceAware style={styles.root}>
      <SafeAreaView edges={LEGAL_SAFE_AREA_EDGES} style={styles.safeArea}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <LegalHeader
            title={content.title}
            subtitle={content.subtitle}
            iconName={content.iconName}
            onBack={() => navigation.goBack()}
            paddingTop={insets.top + Spacing.sm}
          />

          <View style={styles.sections}>{renderLegalSections(content.sections)}</View>

          <ContactFooter paddingBottom={insets.bottom} />
        </ScrollView>
      </SafeAreaView>
    </TonyteBackground>
  );
};

// ─── Community Guidelines ─────────────────────────────────────────────────────

type CommunityGuidelinesProps = NativeStackScreenProps<
  RootStackParamList,
  'CommunityGuidelines'
>;

export const CommunityGuidelinesScreen: React.FC<CommunityGuidelinesProps> = ({
  navigation,
}) => {
  const insets = useSafeAreaInsets();
  const { language } = useI18n();
  const content = resolveLegalContent(communityGuidelinesContent, language);

  return (
    <TonyteBackground appearanceAware style={styles.root}>
      <SafeAreaView edges={LEGAL_SAFE_AREA_EDGES} style={styles.safeArea}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <LegalHeader
            title={content.title}
            subtitle={content.subtitle}
            iconName={content.iconName}
            onBack={() => navigation.goBack()}
            paddingTop={insets.top + Spacing.sm}
          />

          <View style={styles.sections}>{renderLegalSections(content.sections)}</View>

          <ContactFooter paddingBottom={insets.bottom} />
        </ScrollView>
      </SafeAreaView>
    </TonyteBackground>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  root: { flex: 1 },
  safeArea: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: {
    paddingHorizontal: Spacing.base,
    ...Platform.select({
      ios: {
        width: '100%',
        alignItems: 'stretch',
      },
    }),
  },
  sections: {
    gap: Spacing.md,
    marginBottom: Spacing.xl,
    ...Platform.select({ ios: { width: '100%', minWidth: 0 } }),
  },
});

const headerStyles = StyleSheet.create({
  root: {
    paddingHorizontal: 0,
    paddingBottom: Spacing.xl,
    ...Platform.select({ ios: { width: '100%', minWidth: 0 } }),
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    marginBottom: Spacing.md,
    paddingVertical: Spacing.xs,
    paddingHorizontal: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  backLabel: {
    color: 'rgba(255,255,255,0.80)',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
  card: {
    borderRadius: 24,
    padding: 0,
    overflow: 'hidden',
    ...Platform.select({ ios: { width: '100%', minWidth: 0 } }),
  },
  content: { padding: Spacing.lg },
  frame: { width: '100%', minWidth: 0, padding: 0 },
  cardBorder: {
    ...StyleSheet.absoluteFill,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    marginBottom: Spacing.sm,
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  appName: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 10,
    fontWeight: Typography.semibold,
    letterSpacing: 2,
  },
  title: {
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.bold,
    marginTop: 2,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: Typography.sm,
    lineHeight: 22,
  },
  datePill: {
    marginTop: Spacing.md,
    alignSelf: 'flex-start',
    paddingVertical: 5,
    paddingHorizontal: Spacing.md,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  dateText: {
    color: 'rgba(255,255,255,0.60)',
    fontSize: Typography.xs,
  },
});

const sectionStyles = StyleSheet.create({
  card: {
    borderRadius: 20,
    padding: 0,
    overflow: 'hidden',
    ...Platform.select({ ios: { width: '100%', minWidth: 0 } }),
  },
  content: { padding: Spacing.lg },
  frame: { width: '100%', minWidth: 0, padding: 0 },
  border: {
    ...StyleSheet.absoluteFill,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    marginBottom: Spacing.md,
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.semibold,
    flex: 1,
  },
  body: { gap: Spacing.sm },
  para: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.sm,
    lineHeight: 22,
  },
  bullets: { gap: Spacing.xs },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Spacing.sm,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.55)',
    marginTop: 8,
    flexShrink: 0,
  },
  bulletText: {
    flex: 1,
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.sm,
    lineHeight: 22,
  },
});

const footerStyles = StyleSheet.create({
  card: {
    borderRadius: 20,
    padding: 0,
    overflow: 'hidden',
    ...Platform.select({ ios: { width: '100%', minWidth: 0 } }),
  },
  content: { padding: Spacing.lg },
  frame: { width: '100%', minWidth: 0, padding: 0 },
  border: {
    ...StyleSheet.absoluteFill,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    marginBottom: Spacing.sm,
  },
  title: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.semibold,
  },
  body: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: Typography.sm,
    lineHeight: 22,
  },
  email: {
    color: Colors.textPrimary,
    textDecorationLine: 'underline',
  },
});

const createLegalThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  control: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.border,
  }),
  border: daylightStyle(theme, { borderColor: theme.colors.cardBorder }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
});
